/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
// deps
import React, { useContext, useRef, useCallback, useState } from "react";

// internal-deps
import {
    ApiContext,
    getBridgeSdk,
    CheckoutContext,
    CheckoutActionTypes,
    CartOverlaysContext,
    AnalyticsContext,
    useSessionStorage,
    recordCustomTrackingNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import { Result } from "@swiggy-private/js-fp";
import {
    ReviewedCartApiResponse,
    COUPON_GROUP_TYPES,
    CouponInfo,
    ICouponsAPIResponse,
    CroutonIds,
    CroutonPriority,
    IAppliedCouponCodes,
} from "@swiggy-private/cart-types";
import {
    CouponMessageCard,
    CouponListingPLWidgetTypes,
    INextBestCoupon,
    ICouponCard,
    ICouponCards,
} from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// helpers
import {
    prepareNativeCartParamsFromReviewedCartResponse,
    updateApplyCoupon,
    updateRemoveCoupon,
    isCouponAppliedSuccessfully,
    getCouponListingVersion,
    isCouponNotApplied,
    getRestaurantId,
    isPORV2Flow,
} from "@cart-client/helpers/Cart";
import { logCouponAppliedEvent } from "@cart-client/helpers/Clevertap";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
import {
    getCouponSuggestionErrorCard,
    isBestCouponGroup,
    isGreatDealCouponGroup,
} from "@cart-client/helpers/CouponListing";
import { getDiscountingToastMessage } from "@cart-client/helpers/Coupon";
import { isRecommendedCouponGroup } from "@swiggy-private/cart-ui";
import { impressionCartToast } from "@cart-client/analytics/events";

// constants
import {
    BUTTON_IDS,
    ERROR_TEXT,
    RemoveCouponModalData,
    DISCOUNTING_TOAST_DURATION,
} from "@cart-client/constants/Coupon";
import { TURBO_CHECKOUT_UPI_INTENT_METHODS } from "@swiggy-private/payment-client";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";
import { IFetchCouponsParams } from "@cart-client/hooks/types";
import { SyncApplyCouponCartParams } from "@cart-client/interfaces/Coupon";
import { CouponListingCouponRemovalPayload } from "@cart-client/interfaces/MROFoodCart";

// hooks
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";

// apis
import { fetchCouponsPublic } from "@cart-client/apis/publicCart";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";

type ResultCartData = Result<ApiResponse<ReviewedCartApiResponse>, Error | ApiResponse>;

const useApplyCoupon = (): {
    performApplyCoupon: () => Promise<void>;
    performRemoveCoupon: () => Promise<void>;
    syncApplyCouponCart: (params: SyncApplyCouponCartParams) => void;
    showCouponModal: boolean;
    toggleCouponModal: VoidFunction;
    showDelightPopup: boolean;
    togglingDelightPopup: VoidFunction;
    updateCouponState: boolean;
    isFetching: boolean;
    coupons: ICouponCard[] | null;
    isApplyingCoupon: boolean;
    isRemovingCoupon?: boolean;
    errorMessage: string;
    fetchCoupons: ({ restaurantId, cartValue, couponCode, subpage, appliedCouponCodes }: IFetchCouponsParams) => void;
    clearErrorMessage: VoidFunction;
    nextBestCoupon: INextBestCoupon | null;
    resetCouponListingPage: VoidFunction;
    handleCouponListingPageCouponRemove: (payload: CouponListingCouponRemovalPayload) => void;
    tncCoupon?: CouponInfo | null;
    setTncCoupon?: (coupon: CouponInfo | null) => void;
    showPendingDiscountingComms: VoidFunction;
    cards: IPLWidgetControllerCards[];
    hasInitiallyFetched: boolean;
} => {
    const apiContextValue = useContext(ApiContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const [updateCouponState, setUpdateCouponState] = useState<boolean>(false);
    const [coupons, setCoupons] = useState<ICouponCard[] | null>(null);
    const [nextBestCoupon, setNextBestCoupon] = useState<INextBestCoupon | null>(null);
    const [cards, setCards] = useState<IPLWidgetControllerCards[]>([]);
    const [isFetching, setFetching] = useState<boolean>(false);
    const hasInitiallyFetched = useRef<boolean>(false);
    const [suggestedCoupons, setSuggestedCoupons] = useState<string[]>([]);
    const [isApplyingCoupon, setApplyingCoupon] = useState<boolean>(false);
    const [isRemovingCoupon, setRemovingCoupon] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [tncCoupon, setTncCoupon] = useState<CouponInfo | null>(null);
    const { cartState, setCartState } = useContext(CartContext);
    const { registerCrouton } = useContext(CartOverlaysContext);
    const { reviewedCart: cartResponse } = cartState;
    const { porFlowXP } = useCartRendererXP(cartResponse);
    const isNewPORFlow = isPORV2Flow(porFlowXP);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setTurboUpiMethods] = useSessionStorage(TURBO_CHECKOUT_UPI_INTENT_METHODS, null);

    const [showCouponModal, setShowCouponModal] = useState<boolean>(false);
    const [showDelightPopup, setShowDelightPopup] = useState<boolean>(false);

    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();

    const apiHost = apiContextValue.host.unwrap();

    const apiSuccess = useCallback(
        (response: ResultCartData): CouponMessageCard | undefined => {
            apiCallRef.current = false;

            if (isMountedRef.current) {
                const cartData = response.unwrap() ?? null;
                if (cartData.data) {
                    // log clevertap event when coupon applied successfully
                    if (isCouponAppliedSuccessfully(cartData.data)) {
                        logCouponAppliedEvent(cartData);
                        recordCustomTrackingNREvent({
                            action: "coupon flow api success response",
                            timestamp: Date.now(),
                            sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
                            statusCode: cartData?.statusCode,
                            statusMessage: cartData?.statusMessage,
                            couponCode: cartData?.data?.coupon_code,
                        });
                    }

                    if (isCouponNotApplied(cartData.data)) {
                        return cartData.data.coupon_details?.coupon_error_card;
                    } else if (!cartData.data.coupon_code) {
                        // This block will execute when a coupon is successfully removed
                        recordCustomTrackingNREvent({
                            action: "remove_coupon_success",
                            timestamp: Date.now(),
                            sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
                            statusCode: cartData?.statusCode,
                            statusMessage: cartData?.statusMessage,
                        });
                    }

                    const additional_context = cartState?.localCart?.cart_journey_ctx?.additional_context;

                    setCartState({
                        type: CartActionTypes.UPDATE_CART,
                        payload: {
                            uniqueIdForCart: randomStringIdGenerator(),
                            localCart:
                                (cartData.data &&
                                    prepareNativeCartParamsFromReviewedCartResponse(cartData.data, {
                                        orderComment: cartState?.localCart?.orderComment,
                                        cutleryComments: cartState?.localCart?.cutleryComments,
                                        cartLatLong: cartState?.localCart?.cartLatLong,
                                        sld: cartState?.localCart?.sld,
                                        userLiveLat: cartState?.localCart?.userLiveLat,
                                        userLiveLng: cartState?.localCart?.userLiveLng,
                                        //   date will only come in case of bulk order
                                        date: cartState?.localCart?.date,
                                        is_membership_plan_removed: cartState?.localCart?.is_membership_plan_removed,
                                        p_segment_id: cartState?.localCart?.p_segment_id,
                                        parent_restaurant_id: cartState?.localCart?.parent_restaurant_id,
                                        cart_initialization_source: cartState?.localCart?.cart_initialization_source,
                                        cart_category_type: cartState?.localCart?.cart_category_type,
                                        cart_journey_ctx: {
                                            cart_initialization_source:
                                                cartState?.localCart?.cart_journey_ctx?.cart_initialization_source,
                                            cart_category_type:
                                                cartState?.localCart?.cart_journey_ctx?.cart_category_type,
                                            request_contexts: cartState?.localCart?.cart_journey_ctx?.request_contexts,
                                            ...(additional_context && { additional_context }),
                                        },
                                        customer_selected_sla_option:
                                            cartState?.localCart?.customer_selected_sla_option,
                                        flowContext: cartState?.localCart?.flowContext,
                                    })) ||
                                null,
                            reviewedCart: cartData,
                        },
                    });
                }
            }
        },
        [isMountedRef, setCartState, cartState],
    );

    const clearErrorMessage = useCallback(() => {
        setErrorMessage("");
    }, []);

    const resetCouponListingPage = useCallback(() => {
        setApplyingCoupon(false);
        clearErrorMessage();
        setSuggestedCoupons([]);
        setCoupons(null);
        setFetching(false);
        setNextBestCoupon(null);
        hasInitiallyFetched.current = false;
    }, [clearErrorMessage]);

    const apiFailure = useCallback(
        (err: unknown, isCouponListingFlow?: boolean) => {
            recordCustomTrackingNREvent({
                action: "coupon flow api failure response",
                errorStack: err,
                timestamp: Date.now(),
                isCouponListingFlow,
                sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
            });
            apiCallRef.current = false;

            if (isMountedRef.current) {
                captureError(err);
                setUpdateCouponState(false);
                setShowCouponModal(false);
                if (isCouponListingFlow) {
                    setErrorMessage(ERROR_TEXT.DEFAULT);
                    setApplyingCoupon(false);
                }
            }
        },
        [isMountedRef],
    );

    const findNextBestCoupon = useCallback(
        (couponCode: string, isPaymentCouponListingFlow?: boolean): INextBestCoupon | null => {
            let couponToSuggest: CouponInfo | undefined;
            let type: COUPON_GROUP_TYPES | null = null;

            // If applied coupon was already suggested, re-show the current suggestion
            const alreadySuggested = suggestedCoupons.find((each: string) => each.includes(couponCode));
            if (!alreadySuggested && nextBestCoupon) {
                return nextBestCoupon;
            }

            // Search for a BEST coupon which is not already suggested to user, if found show that as the suggestion
            const bestCoupon = coupons?.find((coupon) => isBestCouponGroup(coupon.type));
            const greatDealCoupon = coupons?.find((coupon) => isGreatDealCouponGroup(coupon.type));

            // search for the recommended coupon group
            const recommendedCoupon = coupons?.find((coupon) => isRecommendedCouponGroup(coupon.type));

            // If no BEST coupon is available, see if a GREATDEAL coupon can be shown which is not already suggested to user
            // for payment coupon listing flow, we need to show the recommended coupon group
            const couponGroupToSuggest = isPaymentCouponListingFlow ? recommendedCoupon : bestCoupon || greatDealCoupon;

            if (couponGroupToSuggest) {
                const coupon = couponGroupToSuggest.coupons?.find((coupon: CouponInfo) => {
                    const id = couponGroupToSuggest.type + "_" + coupon.title;
                    return !suggestedCoupons.includes(id);
                });

                if (coupon) {
                    const couponId = couponGroupToSuggest.type + "_" + coupon.title;
                    couponToSuggest = coupon;
                    type = couponGroupToSuggest.type;
                    setSuggestedCoupons((prev) => [...prev, couponId]);
                }
            }

            return type && couponToSuggest ? { type, coupon: couponToSuggest } : null;
        },
        [nextBestCoupon, coupons, suggestedCoupons],
    );

    const syncApplyCouponCart = useCallback(
        ({
            couponCode,
            isCouponListingFlow,
            appliedCouponCodes: _appliedCouponCodes,
            isPaymentCouponListingFlow,
        }: {
            couponCode?: string;
            isCouponListingFlow?: boolean;
            appliedCouponCodes?: IAppliedCouponCodes[];
            isPaymentCouponListingFlow?: boolean;
        }) => {
            if (!isMountedRef.current || updateCouponState || apiCallRef.current) {
                return;
            }

            apiCallRef.current = true;
            setApplyingCoupon(true);
            setUpdateCouponState(true);

            // Offer Team has moved to SOS flow hence the trimming of coupon code has moved to client side
            const trimmedCouponCode = couponCode?.trim();

            updateApplyCoupon(apiHost, trimmedCouponCode || "")
                .then(apiSuccess)
                .then((couponError?: CouponMessageCard) => {
                    if (isMountedRef.current) {
                        if (isCouponListingFlow) {
                            if (!couponError) {
                                setShowCouponModal(true);
                                setUpdateCouponState(false);
                                resetCouponListingPage();

                                // we need to reevaluate upi intent methods for turbo checkout
                                // after applying a payment coupon since single_click method
                                // can change post a payment coupon application
                                if (isPaymentCouponListingFlow) {
                                    setTurboUpiMethods(null);
                                }

                                setCheckoutState({
                                    type: CheckoutActionTypes.SHOW_CART,
                                    payload: {
                                        skipCartReload: true,
                                    },
                                });
                            } else {
                                const nextBest = findNextBestCoupon(couponCode || "", isPaymentCouponListingFlow);
                                const errorMessage = getCouponSuggestionErrorCard(couponError, nextBest);
                                setErrorMessage(errorMessage);
                                setNextBestCoupon(nextBest);
                                setApplyingCoupon(false);
                                setUpdateCouponState(false);
                            }
                        } else {
                            setShowCouponModal(true);
                            setUpdateCouponState(false);
                        }
                    }
                })
                .catch(apiFailure);
        },
        [
            isMountedRef,
            updateCouponState,
            apiHost,
            apiSuccess,
            apiFailure,
            cartResponse?.data?.cart_menu_items,
            cartResponse?.data?.restaurant_details?.id,
            resetCouponListingPage,
            setCheckoutState,
            setTurboUpiMethods,
            findNextBestCoupon,
        ],
    );

    const syncRemoveCouponCart = useCallback(() => {
        if (updateCouponState || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;
        setApplyingCoupon(true);
        setUpdateCouponState(true);

        updateRemoveCoupon(apiHost)
            .then(apiSuccess)
            .then(() => {
                setApplyingCoupon(false);
                setUpdateCouponState(false);
            })
            .catch(apiFailure);
    }, [
        updateCouponState,
        apiHost,
        apiSuccess,
        apiFailure,
        cartResponse?.data?.cart_menu_items,
        cartResponse?.data?.restaurant_details?.id,
    ]);

    const performApplyCouponCart = React.useCallback(async () => {
        const cartData = cartState.reviewedCart?.data;
        try {
            getBridgeSdk()
                .applyCoupon({
                    tenant: { marketplaceBusinessline: "Regular", marketplaceCategory: "Food", marketplaceId: 1 },
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    cart_total_shown_to_coupon: cartData?.cart_total_shown_to_coupon,
                    restaurantId: cartData?.restaurant_details?.id,
                    couponListingVersion: getCouponListingVersion(cartState.reviewedCart),
                })
                .then((response) => {
                    if (response && response.couponCode) {
                        setUpdateCouponState(true);
                        syncApplyCouponCart({ couponCode: response.couponCode });
                    }
                });
        } catch (err) {
            captureError(err);
        }
    }, [syncApplyCouponCart, cartState.reviewedCart]);

    const performRemoveCouponCart = React.useCallback(async () => {
        try {
            const isAutoAppliedCoupon = cartState.reviewedCart?.data?.coupon_details?.is_coupon_auto_applied || false;

            // if coupon is not auto applied, don't need to show coupon modal
            if (!isAutoAppliedCoupon) {
                syncRemoveCouponCart();
                return;
            }

            getBridgeSdk()
                .confirm({
                    ...RemoveCouponModalData,
                    title: `${cartState.reviewedCart?.data?.coupon_code}, coupon applied!`,
                })
                .then((response) => {
                    if (response && response.id === BUTTON_IDS.YES) {
                        syncRemoveCouponCart();
                    }
                });
        } catch (err) {
            captureError(err);
        }
    }, [syncRemoveCouponCart, cartState]);

    const showPendingDiscountingComms = useCallback(() => {
        const toastMessage = getDiscountingToastMessage(cartState.reviewedCart?.data);
        if (!isEmpty(toastMessage)) {
            registerCrouton({
                content: {
                    title: toastMessage,
                    durationOfCrouton: DISCOUNTING_TOAST_DURATION,
                    impressionEvent: () => {
                        impressionCartToast(
                            JSON.stringify({ msg: toastMessage }),
                            `${cartState?.reviewedCart?.data?.cartId}`,
                            impressionEvent,
                        );
                    },
                },
                id: CroutonIds.DISCOUNTING_TOAST,
                priority: CroutonPriority.PRIORITY_ORDER_THREE,
            });
        }
    }, [cartState.reviewedCart?.data, impressionEvent, registerCrouton]);

    const toggleCouponModal = useCallback(() => {
        // this is to be shown once coupon modal is closed
        setShowCouponModal((showCouponModal) => !showCouponModal);
    }, []);

    const togglingDelightPopup = useCallback(() => {
        setShowDelightPopup((showDelightPopup) => !showDelightPopup);
    }, []);

    const extractCouponCardsFromResponse = useCallback((response: ICouponsAPIResponse) => {
        const filterCouponCardsFromResponse = (response?.success?.cards as unknown as ICouponCards[])?.filter(
            (each: ICouponCards) => each?.card?.card?.["@type"] === CouponListingPLWidgetTypes.COUPON_CARD,
        ) as ICouponCards[];
        const couponGroupArr = filterCouponCardsFromResponse?.map((each: ICouponCards) => each?.card?.card);
        couponGroupArr && setCoupons(couponGroupArr);
    }, []);

    const extractPLWidgetsFromResponse = useCallback(
        (response: ICouponsAPIResponse) => {
            extractCouponCardsFromResponse(response);
            setCards(response.success?.cards || []);
        },
        [extractCouponCardsFromResponse],
    );

    const fetchCoupons = useCallback(
        ({
            restaurantId,
            cartValue,
            couponCode,
            subpage,
            appliedCouponCodes: _appliedCouponCodes,
        }: IFetchCouponsParams) => {
            if (isEmpty(restaurantId) || cartValue === undefined) return;

            setFetching(true);
            hasInitiallyFetched.current = true;

            const finalCouponCode = couponCode;

            fetchCouponsPublic({
                apiHost,
                cartValue: String(cartValue),
                restaurantId,
                couponCode: finalCouponCode,
                subpage,
                isNewPORFlow,
            })
                .then((response: ICouponsAPIResponse) => {
                    setFetching(false);
                    extractPLWidgetsFromResponse(response);
                })
                .catch(() => setFetching(false));
        },
        [apiHost, extractPLWidgetsFromResponse, isNewPORFlow],
    );

    const handleCouponListingPageCouponRemove = useCallback(
        async ({ subpage, couponCode }: CouponListingCouponRemovalPayload) => {
            if (updateCouponState || apiCallRef.current) {
                return;
            }

            apiCallRef.current = true;
            setRemovingCoupon(true);
            setUpdateCouponState(true);
            setFetching(true);

            try {
                const response = await updateRemoveCoupon(apiHost, couponCode);

                apiSuccess(response);
                const cartData = response.unwrap() ?? null;
                setUpdateCouponState(false);
                if (!isEmpty(cartData)) {
                    const cartTotal = cartData?.data?.cart_total_shown_to_coupon || 0;
                    const restaurantId = cartData ? getRestaurantId(cartData) : "";

                    if (!isEmpty(restaurantId)) {
                        hasInitiallyFetched.current = true;
                        const response = await fetchCouponsPublic({
                            apiHost,
                            cartValue: String(cartTotal),
                            restaurantId,
                            subpage,
                            isNewPORFlow,
                        });

                        extractPLWidgetsFromResponse(response);
                    }
                }
                setFetching(false);
                setRemovingCoupon(false);
            } catch (err) {
                setFetching(false);
                setRemovingCoupon(false);
                apiFailure(err, true);
            }
        },
        [apiFailure, apiHost, apiSuccess, extractPLWidgetsFromResponse, updateCouponState, isNewPORFlow],
    );

    return {
        performApplyCoupon: performApplyCouponCart,
        performRemoveCoupon: performRemoveCouponCart,
        fetchCoupons,
        syncApplyCouponCart,
        showCouponModal,
        toggleCouponModal,
        updateCouponState,
        coupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        clearErrorMessage,
        nextBestCoupon,
        resetCouponListingPage,
        togglingDelightPopup,
        showDelightPopup,
        handleCouponListingPageCouponRemove,
        tncCoupon,
        setTncCoupon,
        showPendingDiscountingComms,
        cards,
        // Indicates whether coupon fetch API has been called at least once.
        // Used to prevent showing "no coupons available" UI before initial fetch completes as well as show no coupons UI after initial fetch completes and data received is empty.
        hasInitiallyFetched: hasInitiallyFetched.current,
    };
};

export default useApplyCoupon;
